/**
 * Feature Promotion Configurations & Message Templates
 * Modular marketing configurations and lead-generation message templates
 * for locked capabilities across GradeMe AI.
 */

/**
 * Standardized, reusable lead generation message template.
 * Can be reused across any feature by providing featureName, institute, and user details.
 */
export const generateFeatureInquiryMessage = ({
  featureName = "Library Bank",
  instituteName = "Our Institute",
  instituteCode = "",
  userName = "Instructor",
  userEmail = "",
  userRole = "instructor",
}) => {
  const cleanInst = instituteName?.trim() || "Our Institute";
  const instDisplay = instituteCode ? `${cleanInst} (${instituteCode})` : cleanInst;
  const cleanFeature = featureName || "Library Bank";

  return [
    "Hello GradeMe AI Team,",
    "",
    `We are from ${cleanInst} and we are interested in enabling the ${cleanFeature} feature for our institute.`,
    "",
    `Institute Name: ${instDisplay}`,
    `Instructor: ${userName || "Instructor"}`,
    userEmail ? `Email: ${userEmail}` : null,
    "",
    `Kindly guide us with the details and steps to activate the ${cleanFeature} for our account.`,
    "",
    "Thank you!",
  ]
    .filter((line) => line !== null)
    .join("\n");
};

/**
 * Standardized email subject generator.
 */
export const generateFeatureInquirySubject = ({
  featureName = "Library Bank",
  instituteName = "Our Institute",
}) => `Activation Request: ${featureName} - ${instituteName}`;

export const COMPETITIVE_EXAM_LIBRARY_FEATURE = {
  featureName: "Library Bank",
  badgeText: "PREMIUM QUESTION REPOSITORY",
  badgeIcon: "fa-solid fa-lock",
  tagline: "Competitive Exam Practice",
  title: "Practice for JEE, NEET & CUET on GradeMe AI",
  subtitle:
    "Equip your faculty and students with authentic, syllabus-aligned question banks, chapter drills, and full-length simulated mock tests.",
  interactiveTabs: [
    {
      id: "jee",
      label: "JEE (Main & Adv)",
      icon: "fa-solid fa-atom",
      badge: "Engineering Stream",
      headline: "Authentic Practice for JEE Aspirants",
      subjects: "Physics • Chemistry • Mathematics",
      highlights: [
        "Single-choice, multi-correct & numerical value (NVQ) questions",
        "Official NTA pattern marking (+4 for correct, -1 for wrong)",
        "Chapter-wise & full-syllabus simulated exam series",
      ],
    },
    {
      id: "neet",
      label: "NEET-UG",
      icon: "fa-solid fa-stethoscope",
      badge: "Medical Stream",
      headline: "Comprehensive Drills for Medical Aspirants",
      subjects: "Physics • Chemistry • Botany • Zoology",
      highlights: [
        "100% NCERT-based concept coverage & diagrammatic questions",
        "Assertion-Reason & match-the-following practice drills",
        "Section-timed test simulation to build speed and accuracy",
      ],
    },
    {
      id: "cuet",
      label: "CUET-UG",
      icon: "fa-solid fa-graduation-cap",
      badge: "Central Universities",
      headline: "Targeted Prep for University Entrances",
      subjects: "Domain Subjects • General Test • Languages",
      highlights: [
        "Standardized NTA syllabus alignment across key domain subjects",
        "Speed tests and sectional drills to maximize percentile scores",
        "Instant score analytics and pedagogical answer explanations",
      ],
    },
  ],
  benefits: [
    {
      icon: "fa-solid fa-bullseye",
      title: "Real Exam Pattern Simulation",
      desc: "Authentic negative marking, sectional timers & official test layouts.",
    },
    {
      icon: "fa-solid fa-lightbulb",
      title: "Step-by-Step Solutions",
      desc: "Complete pedagogical explanations for every problem to resolve student doubts.",
    },
    {
      icon: "fa-solid fa-bolt",
      title: "Instant Test Deployment",
      desc: "Assemble chapter drills or full-length mock exams in 3 clicks.",
    },
  ],
};


export const HOMEWORK_GRADING_FEATURE = {
  featureName: "Homework Grading",
  badgeText: "PREMIUM CAPABILITY",
  badgeIcon: "fa-solid fa-lock",
  tagline: "Homework & Assignment Grader",
  title: "Unlock Homework Grading on GradeMe AI",
  subtitle:
    "Enable automated AI grading, rubric-based evaluation, and detailed student feedback for daily homework and practice assignments.",
  interactiveTabs: [
    {
      id: "ai_grading",
      label: "AI Homework Grading",
      icon: "fa-solid fa-wand-magic-sparkles",
      badge: "Fast Evaluation",
      headline: "Grade Daily Homework in Minutes",
      subjects: "All Subjects • Handwritten & Digital Submissions",
      highlights: [
        "Automated step-by-step grading of student homework submissions",
        "Consistent rubric alignment with instant error identification",
        "Personalized constructive feedback for every student",
      ],
    },
    {
      id: "analytics",
      label: "Progress Tracking",
      icon: "fa-solid fa-chart-line",
      badge: "Actionable Insights",
      headline: "Track Homework Completion & Mastery",
      subjects: "Student Performance • Class Trends",
      highlights: [
        "Spot struggling students before exam day with submission analytics",
        "Track assignment completion and common misconception trends",
        "Export homework scorecards and reports for parents",
      ],
    },
  ],
  benefits: [
    {
      icon: "fa-solid fa-clock",
      title: "Save Hours of Manual Grading",
      desc: "Evaluate entire batches of homework assignments in a fraction of the time.",
    },
    {
      icon: "fa-solid fa-comment-dots",
      title: "Detailed Student Feedback",
      desc: "Provide clear, constructive feedback so students understand and correct mistakes.",
    },
  ],
};
